import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../assets/css/common.css';
import { adminService } from '../services/adminService';
import { formatYen } from '../utils/format';
import customFetch from '../utils/fetch';
import { API_ENDPOINTS } from '../constants/api-endpoint';
import { toWareki } from '../utils/wareki';
import { qualificationService } from '../services/qualificationService';

const TYPE_LABEL = {
  member: 'メンバー登録 詳細',
  route: '交通経路 詳細',
  photo: '画像使用許諾 詳細',
};

export default function MemberDetail() {
  const navigate = useNavigate();
  const { id, type } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [branches, setBranches] = useState([]);
  const [editingBasic, setEditingBasic] = useState(false);
  const [editBranchId, setEditBranchId] = useState('');
  const [editRoleId, setEditRoleId] = useState('');
  const [basicMsg, setBasicMsg] = useState('');
  const [basicErr, setBasicErr] = useState('');
  const [inviteMsg, setInviteMsg] = useState('');
  const [inviteErr, setInviteErr] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resetMsg, setResetMsg] = useState('');
  const [resetErr, setResetErr] = useState('');
  const [qualifications, setQualifications] = useState([]);
  const userRole = parseInt(localStorage.getItem('userRole'), 10);
  const isAdmin = [1,2,3].includes(userRole);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const d = await adminService.getMemberDetail(id);
        setDetail(d);
        if (isAdmin) {
          const bData = await adminService.getBranches();
          setBranches(bData.branches || []);
        }
        try {
          const qData = await qualificationService.getUserQualifications(id);
          setQualifications(qData.qualifications || []);
        } catch (qe) {
          console.error('資格情報取得エラー:', qe);
        }
      } catch (e) {
        setError('詳細の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [id]);

  const title = TYPE_LABEL[type] || '詳細';

  return (
    <div className="container">
      <h1 className="text-primary text-center mb-4">{title}</h1>
      {loading && <p>読み込み中...</p>}
      {error && <p className="text-danger">{error}</p>}
      {detail && (
        <>
          <section className="details-card" style={{ background:'#fff', padding:16, border:'1px solid #eee', borderRadius:8, marginBottom:16 }}>
            <h3>基本情報</h3>
            {basicMsg && <div className="alert alert-success" style={{fontSize:'0.9rem'}}>{basicMsg}</div>}
            {basicErr && <div className="alert alert-danger" style={{fontSize:'0.9rem'}}>{basicErr}</div>}
            <table className="details-table" style={{ width:'100%', borderCollapse:'collapse' }}>
              <tbody>
                <tr><th>氏名</th><td>{detail.user?.name}</td></tr>
                <tr><th>メール</th><td>{detail.user?.email}</td></tr>
                <tr><th>権限</th><td>
                  {editingBasic ? (
                    <select className="form-control form-control-sm" value={editRoleId} onChange={e => setEditRoleId(e.target.value)} style={{maxWidth:200}}>
                      <option value="2">管理者</option>
                      <option value="3">人事</option>
                      <option value="4">支社長</option>
                      <option value="5">一般社員</option>
                    </select>
                  ) : detail.user?.roleName}
                </td></tr>
                <tr><th>支社</th><td>
                  {editingBasic ? (
                    <select className="form-control form-control-sm" value={editBranchId} onChange={e => setEditBranchId(e.target.value)} style={{maxWidth:200}}>
                      {branches.map(b => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  ) : detail.user?.branchName}
                </td></tr>
              </tbody>
            </table>
            {isAdmin && !editingBasic && (
              <button className="btn btn-outline-primary btn-sm mt-2" onClick={() => {
                setEditBranchId(detail.user?.branchId || '');
                setEditRoleId(detail.user?.roleId || '');
                setEditingBasic(true);
                setBasicMsg(''); setBasicErr('');
              }}>支社・権限を変更</button>
            )}
            {editingBasic && (
              <div className="mt-2">
                <button className="btn btn-primary btn-sm" style={{marginRight:8}} onClick={async () => {
                  setBasicErr(''); setBasicMsg('');
                  try {
                    await customFetch(API_ENDPOINTS.ADMIN_MEMBER_DETAIL(id), {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ branchId: Number(editBranchId), roleId: Number(editRoleId) })
                    });
                    setBasicMsg('更新しました。');
                    setEditingBasic(false);
                    // リロード
                    const d = await adminService.getMemberDetail(id);
                    setDetail(d);
                  } catch (err) {
                    setBasicErr(err?.data?.error || '更新に失敗しました。');
                  }
                }}>保存</button>
                <button className="btn btn-secondary btn-sm" onClick={() => setEditingBasic(false)}>キャンセル</button>
              </div>
            )}
          </section>

          <section className="details-card" style={{ background:'#fff', padding:16, border:'1px solid #eee', borderRadius:8, marginBottom:16 }}>
            <h3>メンバー登録</h3>
            {detail.registration ? (
              <table className="details-table" style={{ width:'100%', borderCollapse:'collapse' }}>
                <tbody>
                  <tr><th>フリガナ</th><td>{detail.registration.furigana}</td></tr>
                  <tr><th>生年月日</th><td>
                    {detail.registration.birthdate}
                    {detail.registration.birthdate && toWareki(detail.registration.birthdate) && `（${toWareki(detail.registration.birthdate)}）`}
                  </td></tr>
                  <tr><th>郵便番号</th><td>{detail.registration.postal_code || '-'}</td></tr>
                  <tr><th>住所</th><td>{detail.registration.residence_address}</td></tr>
                  <tr><th>郵送先郵便番号</th><td>{detail.registration.mailing_postal_code || '-'}</td></tr>
                  <tr><th>郵送先</th><td>{detail.registration.mailing_address}</td></tr>
                  <tr><th>最寄り駅</th><td>{detail.registration.nearest_station}</td></tr>
                  <tr><th>電話</th><td>{detail.registration.phone_number}</td></tr>
                  <tr><th>扶養家族の有無</th><td>{detail.registration.has_dependents || '-'}</td></tr>
                  {detail.registration.bank_name && (
                    <>
                      <tr><th>銀行名</th><td>{detail.registration.bank_name}</td></tr>
                      <tr><th>支店名</th><td>{detail.registration.branch_name}</td></tr>
                      <tr><th>口座種別</th><td>{detail.registration.account_type || '-'}</td></tr>
                      <tr><th>口座番号</th><td>{detail.registration.account_number}</td></tr>
                    </>
                  )}
                </tbody>
              </table>
            ) : <p className="text-muted">未登録</p>}
          </section>

          <section className="details-card" style={{ background:'#fff', padding:16, border:'1px solid #eee', borderRadius:8, marginBottom:16 }}>
            <h3>交通経路</h3>
            <table className="kv-table" style={{ margin:'8px 0 16px' }}>
              <tbody>
                <tr><th>申請者氏名</th><td>{detail.route?.applicant || detail.user?.name || '-'}</td></tr>
                <tr><th>現住所</th><td>{detail.route?.address || detail.registration?.residence_address || '-'}</td></tr>
                <tr><th>バス利用</th><td>{detail.route?.busRequired ? 'はい' : 'いいえ'}</td></tr>
                <tr><th>徒歩不可</th><td>{detail.route?.walkingImpossible ? 'はい' : 'いいえ'}</td></tr>
              </tbody>
            </table>
            {detail.route?.attachmentURL && (
              <div className="route-image" style={{margin:'8px 0 16px'}}>
                <img src={detail.route.attachmentURL} alt="最寄り駅への地図/経路" style={{maxWidth:'100%',border:'1px solid #eee',borderRadius:8}} />
              </div>
            )}
            {(() => {
              const rows = (detail.route?.routes || []).filter(r => r && r.routeName);
              return rows.length ? (
                <table className="details-table" style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead><tr><th>路線</th><th>発</th><th>着</th><th>片道</th><th>定期</th></tr></thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i}><td>{r.routeName}</td><td>{r.fromStation}</td><td>{r.toStation}</td><td>{formatYen(r.oneWayCost)}</td><td>{formatYen(r.commuterPassCost)}</td></tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="text-muted">区間は未登録</p>;
            })()}
            {(() => {
              const rows = (detail.route?.routes || []).filter(r => r && r.routeName);
              if (!rows.length) return null;
              const total = rows.reduce((sum, r) => sum + (Number(r.commuterPassCost) || 0), 0);
              return <p style={{marginTop:8}}><strong>定期代合計: {formatYen(total)}</strong></p>;
            })()}
          </section>

          <section className="details-card" style={{ background:'#fff', padding:16, border:'1px solid #eee', borderRadius:8, marginBottom:16 }}>
            <h3>同意状況</h3>
            <div style={{marginBottom:16}}>
              <p><strong>写真使用許諾:</strong> {{ 1: '承諾する', 2: '承諾しない' }[detail.consents?.photoPermission] ?? '未登録'}</p>
              {isAdmin && detail.consents?.photoSignature && (
                <div style={{marginTop:8}}>
                  <p style={{fontSize:'0.85rem',color:'#666',marginBottom:4}}>署名画像:</p>
                  <img src={detail.consents.photoSignature} alt="写真許諾署名" style={{maxWidth:'100%',maxHeight:200,border:'1px solid #dee2e6',borderRadius:4,background:'#fff'}} />
                </div>
              )}
            </div>
            <div>
              <p><strong>機密保持誓約:</strong> {detail.consents?.hasConfidentiality ? '同意済み' : '未同意'}</p>
              {isAdmin && detail.consents?.confidentialitySignature && (
                <div style={{marginTop:8}}>
                  <p style={{fontSize:'0.85rem',color:'#666',marginBottom:4}}>署名画像:</p>
                  <img src={detail.consents.confidentialitySignature} alt="機密保持署名" style={{maxWidth:'100%',maxHeight:200,border:'1px solid #dee2e6',borderRadius:4,background:'#fff'}} />
                </div>
              )}
            </div>
          </section>

          <section className="details-card" style={{ background:'#fff', padding:16, border:'1px solid #eee', borderRadius:8, marginBottom:16 }}>
            <h3>資格取得報告</h3>
            {qualifications.length === 0 ? (
              <p className="text-muted">報告された資格はありません。</p>
            ) : (
              <div style={{ overflowX:'auto' }}>
                <table className="details-table" style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr><th>資格名</th><th>取得年月日</th><th>登録番号</th><th>証跡</th><th>備考</th></tr>
                  </thead>
                  <tbody>
                    {qualifications.map((q) => (
                      <tr key={q.id}>
                        <td>{q.name}</td>
                        <td style={{whiteSpace:'nowrap'}}>
                          <div>{q.acquiredOn}</div>
                          <div className="text-small text-muted">{toWareki(q.acquiredOn)}</div>
                        </td>
                        <td>{q.certificateNumber || '-'}</td>
                        <td>
                          {q.hasAttachment ? (
                            <button className="btn btn-link btn-sm p-0" onClick={async () => {
                              try {
                                const data = await qualificationService.getAttachment(q.id);
                                const win = window.open();
                                if (!win) return;
                                if ((data.attachmentMime || '').startsWith('image/')) {
                                  win.document.write(`<title>${data.attachmentName || '証跡'}</title><img src="${data.attachment}" style="max-width:100%">`);
                                } else {
                                  win.location.href = data.attachment;
                                }
                              } catch (e) {
                                console.error('証跡取得エラー:', e);
                              }
                            }}>{q.attachmentName || '証跡を表示'}</button>
                          ) : (
                            <span className="text-muted">なし</span>
                          )}
                        </td>
                        <td style={{maxWidth:220, wordBreak:'break-all'}}>{q.note || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {isAdmin && (
            <section className="details-card" style={{ background:'#fff', padding:16, border:'1px solid #eee', borderRadius:8, marginBottom:16 }}>
              <h3>変更履歴</h3>
              {(detail.changeLogs || []).length === 0 ? (
                <p className="text-muted">変更履歴はありません。</p>
              ) : (
                <div style={{ overflowX:'auto' }}>
                  <table className="details-table" style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead>
                      <tr><th>日時</th><th>操作</th><th>項目</th><th>変更前</th><th>変更後</th><th>操作者</th></tr>
                    </thead>
                    <tbody>
                      {detail.changeLogs.map((log, i) => (
                        <tr key={i}>
                          <td style={{whiteSpace:'nowrap'}}>{log.createdAt}</td>
                          <td>{log.action === 'create' ? '登録' : '変更'}</td>
                          <td>{log.field_label || '-'}</td>
                          <td style={{maxWidth:220, wordBreak:'break-all'}}>{log.old_value ?? '-'}</td>
                          <td style={{maxWidth:220, wordBreak:'break-all'}}>{log.new_value ?? '-'}</td>
                          <td>{log.actorName || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          {isAdmin && !detail.user?.hasVisitedWelcome && (
            <section className="details-card" style={{ background:'#fff', padding:16, border:'1px solid #eee', borderRadius:8, marginBottom:16 }}>
              <h3>招待メール</h3>
              {inviteMsg && <div className="alert alert-success" style={{fontSize:'0.9rem'}}>{inviteMsg}</div>}
              {inviteErr && <div className="alert alert-danger" style={{fontSize:'0.9rem'}}>{inviteErr}</div>}
              <p style={{fontSize:'0.9rem'}} className="text-muted">このメンバーはまだログインしていません。招待メールを再送できます。</p>
              <button className="btn btn-info btn-sm" disabled={inviteSending} onClick={async () => {
                setInviteMsg(''); setInviteErr(''); setInviteSending(true);
                try {
                  await customFetch(API_ENDPOINTS.NEW_MEMBER + '/resend-invite', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ userId: Number(id) })
                  });
                  setInviteMsg('招待メールを再送しました。');
                } catch (err) {
                  setInviteErr(err?.data?.error || 'メール送信に失敗しました。');
                } finally {
                  setInviteSending(false);
                }
              }}>{inviteSending ? '送信中...' : '招待メールを再送'}</button>
            </section>
          )}

          {isAdmin && (
            <section className="details-card" style={{ background:'#fff', padding:16, border:'1px solid #eee', borderRadius:8, marginBottom:16 }}>
              <h3>パスワード管理</h3>
              {resetMsg && <div className="alert alert-success" style={{fontSize:'0.9rem'}}>{resetMsg}</div>}
              {resetErr && <div className="alert alert-danger" style={{fontSize:'0.9rem'}}>{resetErr}</div>}
              {!showResetForm ? (
                <button className="btn btn-warning btn-sm" onClick={() => { setShowResetForm(true); setResetMsg(''); setResetErr(''); }}>
                  パスワードをリセット
                </button>
              ) : (
                <div>
                  <div className="mb-2">
                    <input
                      type="password"
                      className="form-control form-control-sm"
                      placeholder="新しいパスワード（8文字以上）"
                      value={resetPassword}
                      onChange={e => setResetPassword(e.target.value)}
                      style={{maxWidth:300}}
                    />
                  </div>
                  <button className="btn btn-danger btn-sm" style={{marginRight:8}} onClick={async () => {
                    setResetErr(''); setResetMsg('');
                    if (resetPassword.length < 8) { setResetErr('パスワードは8文字以上必要です。'); return; }
                    try {
                      await customFetch(API_ENDPOINTS.PASSWORD_RESET(id), {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ newPassword: resetPassword })
                      });
                      setResetMsg('パスワードをリセットしました。');
                      setResetPassword('');
                      setShowResetForm(false);
                    } catch (err) {
                      setResetErr(err?.data?.error || 'パスワードリセットに失敗しました。');
                    }
                  }}>リセット実行</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setShowResetForm(false); setResetPassword(''); }}>キャンセル</button>
                </div>
              )}
            </section>
          )}
        </>
      )}

      <div className="button-group mt-3" style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>一覧へ戻る</button>
        <button className="btn btn-primary" onClick={() => navigate('/member-list')}>メンバー一覧</button>
      </div>
    </div>
  );
}
